import React, { useState, useEffect } from 'react';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';
import './Account.css';

interface SubscriptionStatus {
  status: string;
  tier: string;
  stripe_customer_id?: string;
}

interface UserStats {
  total_photos: number;
  storage_used_bytes: number;
  api_calls_this_month?: number;
}

export const Account: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    loadAccountData();
  }, []);

  const loadAccountData = async () => {
    setLoading(true);
    try {
      // Load user info
      const currentUser = await getCurrentUser();

      // Get email from JWT token claims (works for both Google OAuth and email/password)
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload?.email as string;

      setUser({
        ...currentUser,
        email: email || currentUser.signInDetails?.loginId || currentUser.username
      });

      // Load subscription status
      await loadSubscription();

      // Load usage stats
      await loadStats();
    } catch (error) {
      console.error('Failed to load account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/subscription/status',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        }
      });
      const { body } = await restOperation.response;
      const response = (await body.json()) as any;
      setSubscription(response);
    } catch (error) {
      console.error('Failed to load subscription:', error);
      // Default to free tier if error
      setSubscription({ status: 'none', tier: 'free' });
    }
  };

  const loadStats = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/stats',
        options: {
          queryParams: { period: '30d' },
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          }
        },
      });
      const { body } = await restOperation.response;
      const response = (await body.json()) as any;
      setStats({
        total_photos: response.total_photos || 0,
        storage_used_bytes: response.storage_used_bytes || 0,
        api_calls_this_month: response.api_calls_this_month || 0,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStats({ total_photos: 0, storage_used_bytes: 0 });
    }
  };

  const handleManageBilling = async () => {
    if (!subscription?.stripe_customer_id) {
      alert('No active subscription found. Please subscribe first.');
      return;
    }

    setPortalLoading(true);
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const restOperation = post({
        apiName: 'PhotosynthesizeAPI',
        path: '/subscription/portal',
        options: {
          headers: {
            Authorization: token ? `Bearer ${token}` : ''
          },
          body: {
            customerId: subscription.stripe_customer_id,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = (await body.json()) as any;

      // Redirect to Stripe customer portal
      window.location.href = response.portalUrl;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    try {
      await updatePassword({
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess(true);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Failed to change password:', error);
      setPasswordError(error.message || 'Failed to change password. Please check your current password.');
    }
  };

  const handleExportData = async () => {
    try {
      const restOperation = get({
        apiName: 'PhotosynthesizeAPI',
        path: '/user/export',
      });
      const { body } = await restOperation.response;
      const response = (await body.json()) as any;

      // Create download link
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photosynthesize-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. Please try again or contact support.');
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getTierName = (tier: string): string => {
    const tierNames: { [key: string]: string } = {
      free: 'Free',
      archive: 'Archive',
      workflow: 'Workflow',
      pro: 'Pro',
    };
    return tierNames[tier] || tier;
  };

  const getTierPrice = (tier: string): string => {
    const tierPrices: { [key: string]: string } = {
      free: '$0',
      archive: '$9',
      workflow: '$29',
      pro: '$49',
    };
    return tierPrices[tier] || '$0';
  };

  const getTierLimits = (tier: string) => {
    const limits: { [key: string]: { photos: string; storage: string } } = {
      free: { photos: '250/month', storage: '10 GB' },
      archive: { photos: '2,500/month', storage: '200 GB' },
      workflow: { photos: '10,000/month', storage: '1 TB' },
      pro: { photos: 'Unlimited', storage: 'Unlimited' },
    };
    return limits[tier] || limits.free;
  };

  if (loading) {
    return (
      <div className="account-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading account information...</p>
        </div>
      </div>
    );
  }

  const tierLimits = getTierLimits(subscription?.tier || 'free');

  return (
    <div className="account-page">
      <h1>Account</h1>
      <p className="account-subtitle">Manage your profile and subscription</p>

      {/* Profile Section */}
      <div className="account-section">
        <h2>👤 Profile</h2>

        <div className="profile-grid">
          <div className="profile-item">
            <label>Email</label>
            <div className="profile-value">
              {user?.email || 'N/A'}
            </div>
          </div>

          <div className="profile-item">
            <label>Account Status</label>
            <div className="profile-value">
              <span className="status-badge active">Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Section */}
      <div className="account-section">
        <h2>💳 Subscription</h2>

        <div className="subscription-card">
          <div className="subscription-header">
            <div>
              <h3 className="subscription-tier">{getTierName(subscription?.tier || 'free')} Plan</h3>
              <p className="subscription-price">
                {getTierPrice(subscription?.tier || 'free')}/month
              </p>
            </div>
            <span
              className={`subscription-status ${
                subscription?.status === 'active' ? 'active' : 'inactive'
              }`}
            >
              {subscription?.status === 'active' ? '✓ Active' : subscription?.status || 'Free'}
            </span>
          </div>

          <div className="subscription-features">
            <div className="feature-item">
              <span className="feature-label">Monthly Photos:</span>
              <span className="feature-value">{tierLimits.photos}</span>
            </div>
            <div className="feature-item">
              <span className="feature-label">Storage Limit:</span>
              <span className="feature-value">{tierLimits.storage}</span>
            </div>
            {subscription?.tier === 'workflow' || subscription?.tier === 'pro' ? (
              <>
                <div className="feature-item">
                  <span className="feature-label">Burst Culling:</span>
                  <span className="feature-value">✓ Included</span>
                </div>
                <div className="feature-item">
                  <span className="feature-label">AI Quality Scoring:</span>
                  <span className="feature-value">✓ Included</span>
                </div>
              </>
            ) : null}
            {subscription?.tier === 'pro' ? (
              <>
                <div className="feature-item">
                  <span className="feature-label">API Access:</span>
                  <span className="feature-value">✓ Included</span>
                </div>
                <div className="feature-item">
                  <span className="feature-label">Priority Support:</span>
                  <span className="feature-value">✓ Included</span>
                </div>
              </>
            ) : null}
          </div>

          <div className="subscription-actions">
            {subscription?.status === 'active' && subscription?.stripe_customer_id ? (
              <button
                className="btn-primary"
                onClick={handleManageBilling}
                disabled={portalLoading}
              >
                {portalLoading ? 'Loading...' : 'Manage Billing'}
              </button>
            ) : (
              <a href="/pricing" className="btn-primary">
                Upgrade Plan
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Usage Section */}
      <div className="account-section">
        <h2>📊 Usage This Month</h2>

        <div className="usage-grid">
          <div className="usage-card">
            <div className="usage-icon">📷</div>
            <div className="usage-content">
              <div className="usage-value">{stats?.total_photos.toLocaleString() || 0}</div>
              <div className="usage-label">Total Photos</div>
              <div className="usage-limit">Limit: {tierLimits.photos}</div>
            </div>
          </div>

          <div className="usage-card">
            <div className="usage-icon">💾</div>
            <div className="usage-content">
              <div className="usage-value">{formatBytes(stats?.storage_used_bytes || 0)}</div>
              <div className="usage-label">Storage Used</div>
              <div className="usage-limit">Limit: {tierLimits.storage}</div>
            </div>
          </div>

          {subscription?.tier === 'pro' && (
            <div className="usage-card">
              <div className="usage-icon">🔌</div>
              <div className="usage-content">
                <div className="usage-value">
                  {stats?.api_calls_this_month?.toLocaleString() || 0}
                </div>
                <div className="usage-label">API Calls</div>
                <div className="usage-limit">Unlimited</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="account-section danger-zone">
        <h2>⚠️ Account Management</h2>

        <div className="danger-actions">
          <div className="danger-item">
            <div className="danger-info">
              <h3>Change Password</h3>
              <p>Update your account password</p>
            </div>
            <button className="btn-secondary" onClick={() => setShowPasswordModal(true)}>
              Change Password
            </button>
          </div>

          <div className="danger-item">
            <div className="danger-info">
              <h3>Download Data</h3>
              <p>Export all your photos and metadata</p>
            </div>
            <button className="btn-secondary" onClick={handleExportData}>
              Request Export
            </button>
          </div>

          <div className="danger-item">
            <div className="danger-info">
              <h3>Delete Account</h3>
              <p>Permanently delete your account and all data</p>
            </div>
            <button className="btn-danger" onClick={() => alert('Account deletion requires confirmation. This feature will be available soon.')}>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Change Password</h2>

            {passwordError && (
              <div className="alert alert-error">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="alert alert-success">
                Password changed successfully!
              </div>
            )}

            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Enter new password (min 8 characters)"
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handlePasswordChange}>
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
